'use client';
import { useRef, useState } from 'react';
import { Modal, Select, Textarea } from '@mantine/core';
import { useCreateSupportTicketMutation } from '../api';
import { SUPPORT_SUBJECTS } from '@scaleup/models';
import { Button } from './Button';
import { useAlert } from '../hooks';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLocation: string;
}

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose,
  currentLocation,
}) => {
  const [subject, setSubject] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();
  const showAlert = useAlert();
  const isSubmitting = useRef(false);

  const handleSubmit = async () => {
    if (isSubmitting.current) return;

    if (!subject || !description) {
      showAlert({
        show: true,
        severity: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields.',
        duration: 3000,
      });
      return;
    }

    isSubmitting.current = true;

    try {
      await createSupportTicket({
        userId: '', // This will be populated by the backend from the authenticated user
        subject,
        location: currentLocation,
        description,
      }).unwrap();

      showAlert({
        show: true,
        severity: 'success',
        title: 'Ticket Submitted',
        message: 'Your support ticket has been submitted successfully. Our team will review it shortly.',
        duration: 4000,
      });

      // Reset form and close modal
      isSubmitting.current = false;
      setSubject('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Failed to create support ticket:', error);
      isSubmitting.current = false;
      showAlert({
        show: true,
        severity: 'error',
        title: 'Submission Failed',
        message: 'Failed to submit your support ticket. Please try again.',
        duration: 4000,
      });
    }
  };

  const handleClose = () => {
    setSubject('');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <div className="text-xl font-bold text-text-primary">
          Submit Support Ticket
        </div>
      }
      size="lg"
      centered
      radius="lg"
      classNames={{
        header: 'pb-4 border-b border-gray-200',
        body: 'pt-6',
      }}
    >
      <div className="flex flex-col gap-6">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Subject <span className="text-red-500">*</span>
          </label>
          <Select
            placeholder="Select a subject"
            data={SUPPORT_SUBJECTS}
            value={subject}
            onChange={(value) => setSubject(value || '')}
            searchable
            clearable
            styles={{
              input: {
                fontSize: '16px',
                padding: '12px',
              },
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <Textarea
            placeholder="Please describe your issue in detail..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minRows={6}
            styles={{
              input: {
                fontSize: '16px',
                padding: '12px',
              },
            }}
          />
          <p className="text-sm text-gray-500 mt-2">
            Minimum 10 characters ({description.length}/10)
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            text="Cancel"
            onClick={handleClose}
            disabled={isLoading}
          />
          <Button
            variant="primary"
            text="Submit Ticket"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!subject || description.length < 10 || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};
